import { useEffect, useRef, useState } from 'react';
import { CopyIcon, CheckIcon } from '@thegpvc/ui';
import { cx, text } from '../styles/theme';

/**
 * Copy-to-clipboard button that confirms with a check for 2s. Single source of
 * truth for the copy affordance used across the dashboard (SessionDetail's
 * summary/session-id, the Setup command blocks, ConstraintCandidates) so the
 * icon, timing, and accessibility labels can't drift between copies.
 *
 * `label` is the accessible name / tooltip in the idle state; on success it
 * flips to `${label} copied`. Clipboard writes can reject in insecure contexts,
 * in which case the state is left unchanged (no error surfaced — the visible
 * text is still selectable).
 */
export function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can fail in insecure contexts; leave state unchanged.
    }
  };

  return (
    <button
      onClick={handleCopy}
      aria-label={copied ? `${label} copied` : label}
      title={copied ? 'Copied' : label}
      className={cx(
        'shrink-0 rounded p-1 transition-colors',
        copied ? 'text-emerald-600 dark:text-emerald-400' : cx(text.secondary, 'hover:text-navy-900 dark:hover:text-white'),
      )}
    >
      {copied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
    </button>
  );
}
